/**
 * Buttons that suit the fabric you are looking at.
 *
 * A sewer choosing cloth is already picturing the finished garment, and buttons are
 * the part she will otherwise go hunting for in a separate shop. Putting the matching
 * ones on the fabric page keeps her inside the idea — and it is the reason the shop
 * carries notions at all.
 *
 * Matching is on colour family: the fabric's observed `colour` field against the colour
 * word in each button's name. Both vocabularies are small, Hebrew and controlled by
 * Katya, so a word list is honest here in a way a guess at the photograph's average
 * pixel would not be: a print with six colours has no single average, and picking one
 * would produce confident nonsense.
 *
 * Neutrals are always offered alongside, under their own heading, because cream, grey
 * and black genuinely do go with everything and a sewer expects to see them.
 */
(function () {
  'use strict';

  /* Colour families. The fabric side reads the `colour` metafield, which was set by
     looking at each flat swatch; the button side reads Atelier's own colour names from
     the product title. Matching on the fabric's *title* was the earlier approach and it
     inherited the wrong colour data, which is how an olive double gauze came to be
     offered pink buttons. */
  var FAMILIES = {
    pink:   ['ורוד', 'פודרה', 'רוז', 'פוקסיה', 'אפרסק', 'בלאש'],
    red:    ['אדום', 'בורדו', 'אמרנט', 'יין'],
    rust:   ['חלודה', 'טרקוטה', 'כתום', 'פרג', 'חמר', 'נחושת', 'ברונזה'],
    brown:  ['חום', 'מוקה', 'פקאן', 'מייפל', 'קרמל', 'שוקולד'],
    sand:   ['חול', 'חרדל', 'בז', 'זהב', 'אוקר', 'קאמל', 'טאופ'],
    green:  ['ירוק', 'מנטה', 'מאצ\u05f3ה', "מאצ'ה", 'אייבי', 'יער', 'זית', 'עשב', 'מרווה', 'חאקי'],
    blue:   ['כחול', 'נייבי', 'קובלט', 'תכלת', 'ג\u05f3ינס', "ג'ינס", 'לילה', 'פלדה', 'טורקיז'],
    purple: ['חציל', 'סגול', 'לילך', 'שזיף'],
    neutral:['שמנת', 'לבן', 'אופטי', 'נטורל', 'אפור', 'סערה', 'פחם', 'אנתרציט', 'שחור', 'אבן']
  };

  /* Cream, grey and black go with anything, so they are offered alongside rather than
     competing with the matched set. */
  var NEUTRALS = ['שמנת', 'לבן', 'אופטי', 'שחור', 'אפור', 'סערה', 'אנתרציט', 'נטורל', 'בז'];

  /* The drawer holds twenty-two buttons and only one true red, so an exact-family
     match on its own leaves a bordeaux poplin with a single suggestion and a plum
     canvas with none. These are the families that sit beside each one on the wheel
     and read as deliberate rather than approximate: terracotta against bordeaux is a
     choice a sewer would make herself. They rank below an exact match, never above
     it, so the truest button is always first. */
  var NEIGHBOURS = {
    red:    ['rust', 'pink'],
    rust:   ['red', 'brown'],
    pink:   ['red', 'purple'],
    purple: ['red', 'blue'],
    brown:  ['rust', 'sand'],
    sand:   ['brown', 'green'],
    green:  ['sand']
  };

  /* Buttons Katya does not want beside a particular fabric, by the fabric's handle and a
     word from the button's Hebrew name. 13 Oct 2026: on N° 52 Mira the two storm-grey
     buttons (Joy Glitter, Halo) read as light blue against the navy and do not suit it. */
  var EXCLUDE = {
    'cotton-satin-flowers-navy': ['סערה']
  };

  /* Buttons Katya chose by hand for a fabric, in her order, by a piece of each button's
     Hebrew name. They are the only buttons shown for that fabric: no colour-matched and no neutral row.
     13 Oct 2026, N° 52 Mira: cobalt and powder Glossy, light-clay Palm in 20 and 15 mm. */
  var PICKS = {
    'cotton-satin-flowers-navy': ['גלוסי · קובלט · 14', 'גלוסי · פודרה · 18', 'פאלם · חמר בהיר · 20', 'פאלם · חמר בהיר · 15']
  };

  /* Whole words, not substrings. Hebrew colour names sit inside one another —
     חול (sand) inside כחול (navy), יין (wine) inside שיין — and an indexOf match sent
     mustard buttons to a night-blue crepe and ivy-green ones to every red. Splitting on
     everything that is not a Hebrew letter leaves clean tokens, since both vocabularies
     separate words with spaces, a maqaf, a slash or the middot in a product name. The
     geresh is trimmed at the edges only: בז׳ should read as בז, while מאצ׳ה must not
     come apart. */
  function words(text) {
    return String(text || '')
      .split(/[^\u05D0-\u05EA'\u05F3]+/)
      .map(function (w) { return w.replace(/^['\u05F3]+|['\u05F3]+$/g, ''); })
      .filter(Boolean);
  }

  function familiesOf(text) {
    var ws = words(text);
    var found = [];
    Object.keys(FAMILIES).forEach(function (fam) {
      for (var i = 0; i < FAMILIES[fam].length; i++) {
        if (ws.indexOf(FAMILIES[fam][i]) !== -1) { found.push(fam); return; }
      }
    });
    return found;
  }

  function isNeutral(text) {
    var ws = words(text);
    for (var i = 0; i < NEUTRALS.length; i++) {
      if (ws.indexOf(NEUTRALS[i]) !== -1) return true;
    }
    return false;
  }

  function money(agorot) {
    return '₪' + (agorot / 100).toFixed(2).replace(/\.00$/, '');
  }

  /* The shop root for the current language: '/' in Hebrew, '/ru/' in Russian. Product
     names, types and links all follow it, so the Russian page lists Russian names. */
  function lbRoot() {
    var r = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return r.charAt(r.length - 1) === '/' ? r : r + '/';
  }

  /* /products.json carries availability and price on the variant, not on the product.
     The earlier check read p.available, which is simply undefined there, so five
     unpriced out-of-stock buttons passed straight through and rendered at ₪0. A
     suggestion the shopper cannot buy is worse than one fewer suggestion. */
  function sellable(p) {
    return (p.variants || []).some(function (v) {
      return v.available && parseFloat(v.price) > 0;
    });
  }

  function card(p) {
    var img = (p.images && p.images[0]) ? p.images[0].src : '';
    var buyable = (p.variants || []).filter(function (v) {
      return v.available && parseFloat(v.price) > 0;
    })[0];
    var price = buyable ? Math.round(parseFloat(buyable.price) * 100) : 0;
    // The size is part of the identity of a button, so the full title stays.
    var a = document.createElement('a');
    a.className = 'lb-notion';
    a.href = lbRoot() + 'products/' + p.handle;
    a.innerHTML =
      '<span class="lb-notion__media">' +
        (img ? '<img src="' + img + '" alt="" loading="lazy" width="200" height="200">' : '') +
      '</span>' +
      '<span class="lb-notion__name"></span>' +
      '<span class="lb-notion__price"></span>';
    a.querySelector('.lb-notion__name').textContent = p.title;
    a.querySelector('.lb-notion__price').textContent = money(price);
    return a;
  }

  function build(host, matched, neutral) {
    if (!matched.length && !neutral.length) return;

    // One row under the section heading, matched buttons first, then the neutrals. The two
    // sub-headings ("בגוון הבד", "ומה שתמיד מתאים") were removed at Katya's request, 13 Oct 2026.
    var row = document.createElement('div');
    row.className = 'lb-notions__row';
    matched.slice(0, 4).concat(neutral.slice(0, 4)).forEach(function (p) { row.appendChild(card(p)); });
    host.appendChild(row);
    host.hidden = false;
  }

  function init() {
    var host = document.getElementById('lb-notions');
    if (!host || host.dataset.done) return;

    var data = {};
    try {
      var el = document.getElementById('lb-product-data');
      if (el) data = JSON.parse(el.textContent);
    } catch (e) { return; }

    // Only fabrics get button suggestions. Suggesting buttons on a button is silly.
    if (!data.title || (data.type !== 'בדים' && data.type !== 'Ткани')) return;
    host.dataset.done = '1';

    /* Matching always runs on the Hebrew vocabulary, in every language. On the Russian
       site the colour field and the button names arrive translated, so the Hebrew
       word lists above would find nothing. Rather than keep a second word list, the
       Russian page asks the Hebrew shop for the two things it needs, the fabric's
       colour and the button names, and uses the Russian list only for what is shown. */
    var root = lbRoot();
    var localized = root !== '/';
    var getJson = function (url) { return fetch(url).then(function (r) { return r.json(); }); };

    var hebrewList = getJson('/products.json?limit=250');
    var shownList = localized ? getJson(root + 'products.json?limit=250') : hebrewList;

    var colourText = Promise.resolve(data.colour || data.title);
    if (localized) {
      var handle = (location.pathname.split('/products/')[1] || '').split(/[/?#]/)[0];
      colourText = fetch('/products/' + handle)
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var m = html.match(/id="lb-product-data"[^>]*>\s*([\s\S]*?)\s*<\/script>/);
          var he = m ? JSON.parse(m[1]) : {};
          return he.colour || he.title || '';
        })
        .catch(function () { return ''; });   // neutrals are still offered
    }

    Promise.all([hebrewList, shownList, colourText])
      .then(function (res) {
        var fams = familiesOf(res[2]);
        var shownByHandle = {};
        ((res[1] && res[1].products) || []).forEach(function (p) { shownByHandle[p.handle] = p; });
        var buttons = ((res[0] && res[0].products) || []).filter(function (p) {
          return p.product_type === 'כפתורים' && sellable(p);
        });
        /* Scored rather than filtered: an exact family match scores 2, a neighbouring
           one scores 1, and the row is filled from the top. That way the amaranth
           leads for a bordeaux and the terracotta follows it, instead of the amaranth
           standing alone. */
        var near = {};
        fams.forEach(function (f) {
          (NEIGHBOURS[f] || []).forEach(function (n) {
            if (fams.indexOf(n) === -1) near[n] = true;
          });
        });

        var pageHandle = decodeURIComponent((location.pathname.split('/products/')[1] || '').split(/[/?#]/)[0]);
        var banned = EXCLUDE[pageHandle] || [];

        var scored = [], neutral = [];
        buttons.forEach(function (p) {
          var tw = words(p.title);
          if (banned.some(function (w) { return tw.indexOf(w) !== -1; })) return;
          var bf = familiesOf(p.title);
          var score = 0;
          bf.forEach(function (f) {
            if (fams.indexOf(f) !== -1) score = Math.max(score, 2);
            else if (near[f]) score = Math.max(score, 1);
          });
          var shown = shownByHandle[p.handle] || p;
          if (score) scored.push({ p: shown, score: score });
          else if (isNeutral(p.title)) neutral.push(shown);
        });

        // Stable within a score, so the shop's own product order still shows through.
        scored.sort(function (a, b) { return b.score - a.score; });
        var matched = scored.map(function (x) { return x.p; });
        var picks = PICKS[pageHandle];
        if (picks) {
          var chosen = [];
          picks.forEach(function (piece) {
            var hit = buttons.filter(function (p) { return p.title.indexOf(piece) !== -1; })[0];
            if (hit) chosen.push(shownByHandle[hit.handle] || hit);
          });
          if (chosen.length) {
            matched = chosen;
            neutral = [];              // Katya: only her picks on these fabrics, no neutral row
          }
        }
        build(host, matched, neutral);
      })
      .catch(function () { /* the section stays hidden */ });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}());
